<template>
  <div class="editor">
    <EmbedderCodeManagementModal
      @onSubmit="onAddEmbedCodeToEditor"
      ref="embedderCodeModal" />
    <ImageManagementModal
      @onSubmit="onAddImageURLToEditor"
      ref="editorImageManagementModal" />
    <editor-menu-bubble
      class="menububble"
      :editor="editor"
      @hide="hideLinkMenu"
      v-slot="{ commands, isActive, getMarkAttrs, menu }">
      <div
        class="menububble"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom - 70}px;`">
        <form
          class="menububble__form"
          v-if="linkMenuIsActive"
          @submit.prevent="setLinkUrl(commands.link, linkUrl)">
          <input
            type="text"
            class="menububble__input"
            v-model="linkUrl"
            placeholder="https://"
            ref="linkInput"
            @keydown.esc="hideLinkMenu"/>
          <button
            class="menububble__button"
            @click="setLinkUrl(commands.link, null)"
            type="button">
            <i class="fa fa-remove"/>
          </button>
        </form>
        <template v-else>
          <button
            class="menububble__button"
            @click="showLinkMenu(getMarkAttrs('link'))"
            :class="{ 'is-active': isActive.link() }">
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link'}}</span>
            <i class="fa fa-link ml-2"/>
          </button>
        </template>
      </div>
    </editor-menu-bubble>
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }" class="menu-bar">
      <div class="menubar">
        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.bold() }"
          @click.prevent="commands.bold">
          <i class="fa fa-bold" />
        </button>

        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.italic() }"
          @click.prevent="commands.italic">
          <i class="fa fa-italic" />
        </button>

        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.strike() }"
          @click.prevent="commands.strike">
          <i class="fa fa-strikethrough" />
        </button>

        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.underline() }"
          @click.prevent="commands.underline">
         <i class="fa fa-underline" />
        </button>

        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.code() }"
          @click.prevent="commands.code">
          <i class="fa fa-code" />
        </button>
        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click.prevent="commands.paragraph">
          <i class="fa fa-paragraph" />
        </button>
        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click.prevent="commands.bullet_list">
          <i class="fa fa-list-ul"/>
        </button>
        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click.prevent="commands.ordered_list">
          <i class="fa fa-list-ol"/>
        </button>
        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click.prevent="commands.blockquote">
          <i class="fa fa-quote-right" />
        </button>

        <button
          class="menubar__button"
          type="button"
          :class="{ 'is-active': isActive.horizontal_rule() }"
          @click.prevent="commands.horizontal_rule">
          <i class="fa fa-window-minimize"/>
        </button>

        <button
          class="menubar__button"
          type="button"
          @click.prevent="commands.undo">
          <i class="fa fa-undo" />
        </button>

        <button
          class="menubar__button"
          type="button"
          @click.prevent="commands.redo">
          <i class="fa fa-redo" />
        </button>

        <button
          class="menubar__button"
          type="button"
          @click.prevent="onToggleEmbedderCodeModalShowing(commands.iframe)">
          <i class="fas fa-code" />
        </button>

        <button
          class="menubar__button"
          type="button"
          @click.prevent="onToggleImageURLToEditor(commands.image)">
          <i class="fas fa-image" />
        </button>

      </div>
    </editor-menu-bar>
    <editor-content
      class="editor-content"
      :class="{ disabled: !editable}"
      :editor="editor" />
  </div>
</template>

<script>
import {
  Editor,
  EditorContent,
  EditorMenuBar,
  EditorMenuBubble,
} from 'tiptap';
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Code,
  Italic,
  // Link,
  Strike,
  Underline,
  History,
} from 'tiptap-extensions';
import Iframe from './customExtension/iframe';
import Image from './customExtension/image';
import EmbedderCodeManagementModal from './customExtension/iframe/EmbedderCodeManagementModal.vue';
import ImageManagementModal from './customExtension/image/ImageManagementModal.vue';
import CustomLink from './customExtension/link/CustomLink';

export default {
  props: {
    description: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    EditorContent,
    EditorMenuBar,
    EditorMenuBubble,
    EmbedderCodeManagementModal,
    ImageManagementModal,
  },
  data() {
    return {
      data: '',
      linkUrl: null,
      linkMenuIsActive: false,
      editor: new Editor({
        editable: this.editable,
        content: this.description ? `${this.description}` : '',
        onUpdate: ({ getHTML }) => {
          this.data = getHTML();
        },
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new CustomLink(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          // custom extension
          new Iframe(),
          new Image(),
        ],
      }),
    };
  },
  watch: {
    data() {
      this.$emit('update:description', this.data);
    },
  },
  methods: {
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href;
      this.linkMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.linkInput.focus();
      });
    },
    hideLinkMenu() {
      this.linkUrl = null;
      this.linkMenuIsActive = false;
    },
    setLinkUrl(command, url) {
      command({ href: url });
      this.hideLinkMenu();
    },
    onToggleEmbedderCodeModalShowing(command) {
      this.$refs.embedderCodeModal.onShowEmbedderCodeModal(command);
    },
    onAddEmbedCodeToEditor(data) {
      if (data.command !== null) {
        data.command(data.data);
      }
    },
    onToggleImageURLToEditor(command) {
      this.$refs.editorImageManagementModal.onShowEditorImageManagementModal(command);
    },
    onAddImageURLToEditor(data) {
      if (data.command !== null) {
        data.command(data.data);
      }
    },
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss" scope>
@import './src/assets/scss/variables.scss';

.menubar__button {
  font-weight: 700;
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #000;
  padding: .2rem .5rem;
  margin-right: .2rem;
  border-radius: 3px;
  cursor: pointer;
}

.is-active {
  background: $gray-500;
}

.menu-bar {
  background: $gray-300;
  padding: $spacer * 0.5;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}

.editor-content {
  background-color: white;
  .ProseMirror {
    height: 250px;
    max-height: 250px;
    overflow-y: auto;
    border: 0.25px solid $gray-200;
    padding: $spacer * 0.5 $spacer;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
  }
  .ProseMirror:focus {
    outline-width: 0;
  }

  hr {
    margin: 0.75 * $spacer 0;
  }
}

.disabled {
  .ProseMirror {
    background-color: $gray-200;
  }
}


.menububble {
  position: absolute;
  display: -webkit-box;
  display: flex;
  z-index: 20;
  background: #000;
  border-radius: 5px;
  padding: .3rem;
  margin-bottom: .5rem;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  -webkit-transition: opacity .2s,visibility .2s;
  transition: opacity .2s,visibility .2s;

  &.is-active {
    opacity: 1;
    visibility: visible;
  }
}

.menububble__button {
  display: -webkit-inline-box;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #fff;
  padding: .2rem .5rem;
  margin-right: .2rem;
  border-radius: 3px;
  cursor: pointer;

  display: flex;
  align-items: center;

  &:last-child {
    margin-right: 0;
  }

  &:focus {
    outline: none;
  }
}

.menububble__form {
  display: flex;
}

.menububble__input {
  font: inherit;
  border: none;
  background: transparent;
  color: #fff;

  &:focus {
    outline: none;
  }
}

.iframe {
  &__embed {
    width: 100%;
    height: 15rem;
    border: 0;
  }
  &__input {
    display: block;
    width: 100%;
    font: inherit;
    border: 0;
    border-radius: 5px;
    background-color: rgba($black, 0.1);
    padding: $spacer * 0.3 $spacer * 0.5;
  }
}

.image {
  img {
    width: 100%;
  }

  &__input {
    display: block;
    width: 100%;
    font: inherit;
    border: 0;
    border-radius: 5px;
    background-color: rgba($black, 0.1);
    padding: $spacer * 0.3 $spacer * 0.5;
    margin-top: $spacer * 0.5;
  }
}
</style>
